// ABOUTME: Applies stage changes to the local Kanban and refreshes persisted state.
// ABOUTME: Waits for Elasticsearch to expose the expected applicant stage.
import React from "react";
import { QueryKey, useQueryClient } from "react-query";
import useJobContext from "../../../useJobContext";
import useUpdateCard from "../../useUpdateCard";
import useRefreshKanban from "../../useRefreshKanban";
import {
    findApplicantIndex,
    normalizeApplicantId,
    refreshApplicantProfileAfterStageChange,
    refreshApplicantStageView
} from "../../applicantStageReadConsistency";
import { startApplicantStageChange } from "../../applicantStageMutationGeneration";

const useJobChangeStageFunctionParam = ({
    applicantProfile
}: {
    applicantProfile?: {
        queryKey: QueryKey;
        jobId: string;
    };
} = {}) => {
    const [data] = useJobContext();
    const queryClient = useQueryClient();
    const { changeCardStateData } = useUpdateCard();
    const updateAllInfo = useRefreshKanban();
    const startJobStageChange = (applicantId: string) =>
        startApplicantStageChange(
            data.applicantStageMutationGenerations.current,
            `${data.JobId}:${normalizeApplicantId(applicantId)}`
        );

    const jobChangeStageFunction = async (params: any) => {
        const {
            outsideKanban,
            destinationIndex,
            applicantId,
            stageData,
            newStageId,
            requestId,
            stageChangeShouldApply = () => true
        } = params;
        if (!stageChangeShouldApply()) {
            return;
        }
        if (!outsideKanban) {
            const sourceApplicants =
                data.LocalStateData.ApplicantsData[stageData?.id]?.applicants ||
                [];
            const sourceIndex = findApplicantIndex(
                sourceApplicants,
                applicantId
            );
            if (sourceIndex >= 0) {
                await changeCardStateData(
                    //@ts-ignore
                    {
                        draggableId: applicantId,
                        mode: "FLUID",
                        reason: "DROP",
                        type: "",
                        source: {
                            droppableId: stageData?.id,
                            index: sourceIndex
                        },
                        destination: {
                            droppableId: newStageId,
                            index: destinationIndex
                        }
                    },
                    false
                );
            }
        }
        if (!stageChangeShouldApply()) {
            return;
        }
        const confirmedStage = data.LocalStateData.allStages.find(
            (stage) => stage.id === newStageId
        );
        await refreshApplicantStageView({
            hasJobContext: true,
            refreshKanban: updateAllInfo,
            refreshApplicant: async () => {
                if (
                    stageChangeShouldApply() &&
                    applicantProfile &&
                    confirmedStage
                ) {
                    refreshApplicantProfileAfterStageChange({
                        queryClient,
                        queryKey: applicantProfile.queryKey,
                        jobId: applicantProfile.jobId,
                        requestId,
                        stage: confirmedStage
                    });
                }
            },
            invalidateKanban: () => {
                queryClient.invalidateQueries(data.apiKeyApplicant);
                queryClient.invalidateQueries(data.apiKeyOrderData);
            },
            params: {
                applicantId,
                newStageId,
                requestId
            },
            shouldContinue: stageChangeShouldApply
        });
    };
    return { jobChangeStageFunction, startJobStageChange };
};

export default useJobChangeStageFunctionParam;
